import { useQuery } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { initials } from "@/lib/format";
import { cn } from "@/lib/utils";
import { Panel, SectionHead, SectionShell } from "./_shared";
import { Heart } from "lucide-react";

const TONE = {
  OVERDUE: "bg-pawkt-coral text-pawkt-emergency",
  DUE: "bg-pawkt-butter text-pawkt-secondary-deep",
  APPROACHING: "bg-pawkt-primary-soft text-pawkt-primary-deep",
  RECENTLY_RETURNED: "bg-pawkt-mint text-pawkt-success-deep",
  PRELIMINARY: "bg-pawkt-elevated text-pawkt-text-secondary",
};
const LABEL = {
  OVERDUE: "Overdue", DUE: "Due now", APPROACHING: "Returning soon",
  RECENTLY_RETURNED: "Just returned", PRELIMINARY: "New pattern",
};

export function CustomerSignals({ canReports }) {
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ["return-dashboard"], enabled: canReports,
    queryFn: async () => (await api.get("/return-dashboard")).data,
  });
  if (!canReports) return null;

  return (
    <Panel className="p-5 sm:p-6" data-testid="customer-signals">
      <SectionHead eyebrow="People Behind The Numbers" title="Your customers are coming back"
        action={<button onClick={() => navigate("/action-center")} className="text-xs font-bold text-pawkt-primary-deep hover:underline">Action Center →</button>} />
      <SectionShell query={q}>
        {(d) => {
          const counts = d.counts || {};
          const dueTotal = (counts.DUE || 0) + (counts.OVERDUE || 0);
          const items = (d.items || []).slice(0, 6);
          if (items.length === 0) {
            return <p className="text-sm text-pawkt-text-secondary" data-testid="signals-empty">As customers build a visit history, their return patterns will surface here.</p>;
          }
          return (
            <div>
              {dueTotal > 0 && (
                <div className="mb-4 rounded-2xl pawkt-gradient-mint border border-pawkt-border-soft px-4 py-3">
                  <span className="font-display text-base font-extrabold text-pawkt-text">{dueTotal} {dueTotal === 1 ? "customer is" : "customers are"} due for another visit.</span>
                </div>
              )}
              <div className="space-y-1.5">
                {items.map((c) => (
                  <button key={c.customer_id} data-testid={`signal-${c.customer_id}`} onClick={() => navigate(`/customers/${c.customer_id}`)}
                    className="group flex w-full items-center gap-3 rounded-xl px-2 py-2 text-left hover:bg-pawkt-soft transition-colors">
                    <div className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-pawkt-primary-soft text-xs font-bold text-pawkt-primary-deep">
                      {initials(c.customer_name) || "🐾"}
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="truncate text-sm font-bold text-pawkt-text">{c.customer_name || "Customer"}</div>
                      <div className="truncate text-xs text-pawkt-text-secondary">
                        {c.days_since_last != null ? `${c.days_since_last} days since last visit` : c.explanation || "Return pattern tracked"}
                      </div>
                    </div>
                    <span className={cn("shrink-0 rounded-full px-2.5 py-0.5 text-[11px] font-bold", TONE[c.status] || TONE.PRELIMINARY)}>
                      {LABEL[c.status] || c.status}
                    </span>
                  </button>
                ))}
              </div>
            </div>
          );
        }}
      </SectionShell>
    </Panel>
  );
}
